import dayjs from "dayjs"
import { uniq } from "lodash"
import { useCallback, useMemo } from "react"

import { classAtoms } from "#atoms"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { StudentClassAttendance } from "#customTypes/composedTypes/studentTypes"
import { Uuid } from "#customTypes/globalTypes"
import { Attendance, Semester } from "#customTypes/types"
import { useIsFulfilled } from "#hooks/jotai/useCascadingData"
import { useGetOneStudent } from "#hooks/jotai/useGetStudents"
import { useSomeDocs } from "#hooks/jotai/useSubscription"
import { generateSessionDates, isDefined } from "#utils/classHelpers"
import { setTimeFromString } from "#utils/dateHelpers"

const useStudentAttendanceData = (studentId: Uuid, semester?: Semester) => {
  const student = useGetOneStudent(studentId)

  const attendance = student?.attendance
  const assistantAttendance = student?.assistantAttendance

  const classIds = useMemo(() => {
    return uniq([
      ...Object.keys(attendance || {}),
      ...Object.keys(assistantAttendance || {}),
    ]).sort()
  }, [attendance, assistantAttendance])

  const classData = useSomeDocs<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    classIds
  )

  const isFulfilled = useIsFulfilled(classIds, classData)

  // Newest classes first
  const reverseSortedClassData = useMemo(() => {
    if (!isFulfilled) return []

    return classData
      ?.filter((classObj) => {
        if (!semester) return true
        return classObj.semester === semester
      })
      .slice()
      .sort((a, b) => {
        return dayjs(b.startDate).diff(dayjs(a.startDate))
      })
  }, [classData, isFulfilled, semester])

  const getSortedClassAttendance = useCallback(
    (attendance: Attendance): StudentClassAttendance[] => {
      if (!reverseSortedClassData || !attendance) return []

      return reverseSortedClassData
        .filter((classObj) => attendance[classObj.id])
        .map((classObj) => {
          const sessionDates = generateSessionDates({
            startDate: dayjs(classObj.startDate),
            times: [
              setTimeFromString(classObj.startDate, classObj.startTime)!,
              setTimeFromString(classObj.startDate, classObj.endTime)!,
            ],
            isSummerCamp: classObj.isSummerCamp ?? false,
            numberOfSessions: classObj?.sessionIds?.length ?? 0,
          }).map((session) => dayjs(session.eventStart))

          const attendanceData = attendance[classObj.id]

          const attendanceWithDates = attendanceData.map(
            (session, sessionIndex) => {
              return {
                ...session,
                sessionDate: sessionDates[sessionIndex],
              }
            }
          )

          return {
            classId: classObj.id,
            isSummerCamp: !!classObj.isSummerCamp,
            startDate: classObj.startDate,
            name: classObj.name,
            attendanceSessions: attendanceWithDates,
          }
        })
        .filter(isDefined)
    },
    [reverseSortedClassData]
  )

  const sortedAttendance = useMemo(() => {
    return getSortedClassAttendance(attendance || {})
  }, [attendance, getSortedClassAttendance])

  const sortedAssistantAttendance = useMemo(() => {
    return getSortedClassAttendance(assistantAttendance || {})
  }, [assistantAttendance, getSortedClassAttendance])

  return {
    student,
    sortedAttendance,
    sortedAssistantAttendance,
    isFulfilled,
  }
}

export default useStudentAttendanceData
